"use client";

import { useRouter } from "next/navigation";
import type { FormEvent, ReactNode } from "react";
import { useCallback, useRef, useState } from "react";
import { submitFormData } from "@rosette/web_components/src/form/FormResponse.js";
import type { FormState } from "@rosette/web_components/src/form/FormStateContext.js";
import { FormStateProvider, useFormState } from "@rosette/web_components/src/form/FormStateContext.js";
import { useMutation } from "@tanstack/react-query";
import CheckIcon from "@rosette/web_components/src/icon/CheckIcon.js";
import { cn } from "@rosette/ts/src/util/cn.js";


export function FormStatus() {
    const formState = useFormState();
    return (
        <div className="mt-2 mb-4 flex flex-row items-center justify-start">
            {formState.submitFormError && (
                <p className="m-0 min-h-[1em] text-base text-danger-strong">
                    {formState.submitFormError}
                </p>
            )}

            {formState.submitFormSuccessMessage && (
                <>
                    <CheckIcon className="mr-2 inline size-[2.5em] fill-success" />
                    <p className="m-0 min-h-[1em] text-base">
                        {formState.submitFormSuccessMessage}
                    </p>
                </>
            )}
        </div>
    );
}


export interface FormProps {
    children?: ReactNode;
    className?: string;
    action: string;
    validationError: string | null;
    autoComplete?: string;
    skipRefreshOnSubmit?: boolean;
    acceptResponseJSON?: (json: any) => void;
}


export function Form({
    children, className, action, validationError, autoComplete,
    skipRefreshOnSubmit, acceptResponseJSON,
}: FormProps) {
    const router = useRouter();
    const [successMessage, setSuccessMessage] = useState("");
    const [redirecting, setRedirecting] = useState(false);

    const formRef = useRef<HTMLFormElement | null>(null);

    // Cached data to use when submitting a form on unmount.
    // Not supported unless the user of this Form calls updateCachedFormData.
    const formDataRef = useRef<FormData | undefined>(undefined);
    const updateCachedFormData = useCallback(() => {
        if (formRef.current) {
            formDataRef.current = new FormData(formRef.current);
        }
    }, [formRef, formDataRef]);

    const formMutation = useMutation({
        mutationFn: async (formData: FormData) => {
            // The native required checks run first, so this reports what they
            // cannot see, such as a username that is too short.
            if (validationError)
                throw new Error(validationError);

            setSuccessMessage("");
            const response = await submitFormData(action, formData);
            if (response.redirect) {
                setSuccessMessage("You are being redirected...");
                setRedirecting(true);
                window.location.href = response.redirect;
                return;
            }

            if (!skipRefreshOnSubmit) {
                router.refresh();
            }

            if (response.message) {
                setSuccessMessage(response.message);
                setRedirecting(false);
                if (acceptResponseJSON) {
                    acceptResponseJSON(response.json);
                }
                return;
            }

            let error = response.error;
            if (!error) {
                error = "An unexpected error occurred";
            }
            throw new Error(error);
        },
    });

    const formMutate = formMutation.mutate;
    const submitForm = useCallback(() => {
        let formData;
        if (formRef.current) {
            formData = new FormData(formRef.current);
        } else {
            formData = formDataRef.current;
        }
        if (formData) {
            formMutate(formData);
        }
    }, [formRef, formDataRef, formMutate]);

    const formState: FormState = {
        formValidationError: validationError ?? undefined,
        submitFormError: formMutation.error?.message,
        submitFormIsPending: formMutation.isPending || redirecting,
        submitFormSuccessMessage: successMessage,
        submitForm, updateCachedFormData,
    };

    return (
        <div className={cn(
            "flex w-full flex-col items-start text-left",
            "pt-4",
            className ?? null,
        )}>
            <FormStateProvider formState={formState}>
                <form
                    className="flex w-full flex-col items-start text-left"
                    action={action}
                    method="post"
                    onSubmit={(event: FormEvent<HTMLFormElement>) => {
                        event.preventDefault();
                        const formData = new FormData(event.currentTarget);
                        formMutation.mutate(formData);
                    }}
                    autoComplete={autoComplete}
                    ref={formRef}>

                    {children}
                </form>
            </FormStateProvider>
        </div>
    );
}
